<template>
  <RrModal
    :open="open"
    title="选择跳转目标"
    :width="960"
    :ok-button-props="{ disabled: !canApply }"
    ok-text="应用跳转"
    cancel-text="取消"
    @ok="apply"
    @cancel="close"
    @update:open="onOpenChange"
  >
    <div class="ren-link-target-dialog">
      <div class="ren-link-target-dialog__toolbar">
        <RrTabs
          v-model="selectedCategory"
          :items="categoryTabs"
          size="small"
        />
        <RrInput
          v-model:value.trim="keyword"
          type="search"
          size="small"
          allow-clear
          placeholder="搜索名称、模块或编码"
          aria-label="搜索跳转目标"
        />
      </div>

      <div class="ren-link-target-dialog__body">
        <RrTable
          class="ren-link-target-dialog__table"
          size="small"
          row-key="id"
          :columns="columns"
          :data-source="tableRows"
          :row-selection="rowSelection"
          empty-text="没有符合当前筛选条件的跳转目标"
          :scroll="{ y: 360 }"
          @row-click="onRowClick"
        >
          <template #bodyCell="{ column, record, text }">
            <template v-if="column.key === 'targetName'">
              <div class="ren-link-target-dialog__cell-target" :title="record.targetName">
                <span class="ren-link-target-dialog__name-text">{{ record.targetName }}</span>
                <span
                  v-if="record.groupName && record.groupName !== record.targetName"
                  class="ren-link-target-dialog__group-text"
                  :title="`业务分组：${record.groupName}`"
                >
                  {{ record.groupName }}
                </span>
              </div>
            </template>
            <template v-else-if="column.key === 'category'">
              <div class="ren-link-target-dialog__cell-tags">
                <RrTag
                  :tone="record.category === 'business-page' ? 'warning' : 'primary'"
                >
                  {{ record.categoryLabel }}
                </RrTag>
                <RrTag
                  v-if="record.isDynamicTemplate"
                  tone="info"
                >
                  需配参数
                </RrTag>
              </div>
            </template>
            <template v-else-if="column.key === 'routePath'">
              <span class="ren-link-target-dialog__cell-route" :title="record.routePath">
                {{ record.routePath }}
              </span>
            </template>
            <template v-else>
              {{ text }}
            </template>
          </template>
        </RrTable>

        <aside class="ren-link-target-dialog__aside">
          <RrCard v-if="selectedTarget" size="small" :title="selectedTarget.targetName">
            <template #extra>
              <RrTag
                v-if="selectedTarget.groupName"
                size="small"
              >
                {{ selectedTarget.groupName }}
              </RrTag>
            </template>

            <RrForm layout="vertical" size="small">
              <RrFormItem label="路由">
                <code class="ren-link-target-dialog__route" :title="selectedTarget.routePath">
                  {{ selectedTarget.routePath }}
                </code>
              </RrFormItem>

              <template v-if="paramFields.length > 0">
                <RrFormItem
                  v-for="field in paramFields"
                  :key="field.code"
                  required
                >
                  <template #label>
                    <div class="ren-link-target-dialog__param-header">
                      <span>{{ field.label }}</span>
                      <RrRadioGroup
                        v-if="selectedTarget.targetCode !== MINI_PROGRAM_TARGET_CODE"
                        :value="getParamMode(field.code)"
                        size="small"
                        :options="paramModeOptions"
                        @update:value="setParamMode(field.code, $event as 'fixed' | 'binding')"
                      />
                    </div>
                  </template>

                  <div class="ren-link-target-dialog__param-field">
                    <RrInput
                      v-if="getParamMode(field.code) === 'fixed' || selectedTarget.targetCode === MINI_PROGRAM_TARGET_CODE"
                      :type="field.type === 'number' ? 'number' : 'text'"
                      :value="params[field.code] != null ? String(params[field.code]) : ''"
                      :placeholder="`请输入${field.label}固定值`"
                      allow-clear
                      @update:value="setParam(field.code, field.type, String($event))"
                    />
                    <template v-else>
                      <RrInput
                        :value="paramBindings[field.code] ?? ''"
                        :placeholder="`如 item.${field.code} 或 item.routeId`"
                        allow-clear
                        @update:value="setParamBinding(field.code, String($event))"
                      />
                      <p class="ren-link-target-dialog__param-hint">
                        在列表渲染时，自动从当前数据项读取此字段
                      </p>
                    </template>
                  </div>
                </RrFormItem>
              </template>

              <div v-else class="ren-link-target-dialog__no-params">
                此页面为直达页面，无需额外配置参数。
              </div>

              <RrFormItem v-if="miniProgramError" label="参数错误">
                <span role="alert" class="ren-link-target-dialog__error">{{ miniProgramError }}</span>
              </RrFormItem>

              <div class="ren-link-target-dialog__meta-section">
                <div class="ren-link-target-dialog__meta-item">
                  <span class="ren-link-target-dialog__meta-label">客户端权限：</span>
                  <span>{{ selectedTarget.requiredPermission || '无需额外权限' }}</span>
                </div>
                <div class="ren-link-target-dialog__meta-item">
                  <span class="ren-link-target-dialog__meta-label">登录要求：</span>
                  <span>{{ selectedTarget.loginRequired ? '必须登录' : '允许匿名访问' }}</span>
                </div>
              </div>

              <div v-if="missingParamLabels.length > 0" class="ren-link-target-dialog__prompt">
                请配置必填参数（{{ missingParamLabels.join('、') }}）后应用跳转
              </div>
            </RrForm>
          </RrCard>
          <RrEmpty
            v-else
            compact
            title="未选择目标"
            description="请从左侧列表选择一个已注册页面"
          />
        </aside>
      </div>
    </div>
  </RrModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  RrCard,
  RrEmpty,
  RrForm,
  RrFormItem,
  RrInput,
  RrModal,
  RrRadioGroup,
  RrTable,
  RrTabs,
  RrTag,
  type TableColumnType,
  type TableRowSelection,
} from '@xjlv2/ui'
import type { RenDecorationLinkTargetDefinition, RenDecorationNavigationTarget } from '../../types'
import { parseDecorationFields } from '../utils/editor-fields'
import {
  MINI_PROGRAM_TARGET_CODE,
  miniProgramParamsError,
  miniProgramParamsFromRoute,
  LINK_TARGET_CATEGORY_OPTIONS,
  type LinkTargetApplyPayload,
  type LinkTargetCategory,
  linkTargetByRoutePath,
  linkTargetCategory,
  linkTargetCategoryLabel,
  linkTargetForNavigation,
  linkTargetIdentity,
} from '../utils/link-targets'
import { normalizeSearch } from '../utils/template-market'

type LinkTargetRow = RenDecorationLinkTargetDefinition & {
  id: string
  category: Exclude<LinkTargetCategory, 'all'>
  categoryLabel: string
  isDynamicTemplate: boolean
}

const props = withDefaults(
  defineProps<{
    open?: boolean
    linkTargets?: RenDecorationLinkTargetDefinition[]
    currentNavigation?: RenDecorationNavigationTarget | null
    currentRoutePath?: string
    excludePageCode?: string
    autoBindItemParams?: boolean
    inferParamBinding?: (code: string) => string
  }>(),
  {
    open: false,
    linkTargets: () => [],
    currentNavigation: null,
    currentRoutePath: '',
    excludePageCode: '',
    autoBindItemParams: false,
  },
)

const emit = defineEmits<{
  'update:open': [open: boolean]
  apply: [payload: LinkTargetApplyPayload]
}>()

const selectedCategory = ref('all')
const keyword = ref('')
const selectedId = ref('')
const params = ref<Record<string, string | number | boolean>>({})
const paramBindings = ref<Record<string, string>>({})
const paramModes = ref<Record<string, 'fixed' | 'binding'>>({})

const categoryTabs = LINK_TARGET_CATEGORY_OPTIONS.map((item) => ({
  key: item.value,
  label: item.label,
}))

const paramModeOptions = [
  { label: '固定值', value: 'fixed' },
  { label: '动态绑定', value: 'binding' },
]

const columns: TableColumnType<LinkTargetRow>[] = [
  { key: 'targetName', dataIndex: 'targetName', title: '名称', ellipsis: true },
  { key: 'category', title: '类型', width: 140 },
  { key: 'moduleCode', dataIndex: 'moduleCode', title: '模块', width: 110, ellipsis: true },
  { key: 'routePath', dataIndex: 'routePath', title: '路由', ellipsis: true },
]

const enabledTargets = computed(() =>
  props.linkTargets.filter((target) => {
    if (target.enabled === false) return false
    if (props.excludePageCode && target.pageCode === props.excludePageCode) return false
    return true
  }),
)

const tableRows = computed<LinkTargetRow[]>(() => {
  const query = normalizeSearch(keyword.value)
  return enabledTargets.value
    .filter((target) => selectedCategory.value === 'all' || linkTargetCategory(target) === selectedCategory.value)
    .filter(
      (target) =>
        !query ||
        [
          target.targetName,
          target.targetCode,
          target.moduleCode,
          target.groupName,
          target.targetType,
          linkTargetCategoryLabel(target),
        ].some((value) => normalizeSearch(value).includes(query)),
    )
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((target) => {
      const isDynamic = Boolean(
        target.routePath.includes('{') ||
        (target.paramsSchemaJson && target.paramsSchemaJson !== '[]' && parseDecorationFields(target.paramsSchemaJson).length > 0),
      )
      return {
        ...target,
        id: linkTargetIdentity(target),
        category: linkTargetCategory(target),
        categoryLabel: linkTargetCategoryLabel(target),
        isDynamicTemplate: isDynamic,
      }
    })
})

const selectedTarget = computed(
  () => enabledTargets.value.find((target) => linkTargetIdentity(target) === selectedId.value) ?? null,
)
const paramFields = computed(() => parseDecorationFields(selectedTarget.value?.paramsSchemaJson || '[]'))
const miniProgramError = computed(() => selectedTarget.value?.targetCode === MINI_PROGRAM_TARGET_CODE ? miniProgramParamsError(params.value) : '')

function getParamMode(code: string): 'fixed' | 'binding' {
  return paramModes.value[code] ?? 'fixed'
}

function initParamModes(target: RenDecorationLinkTargetDefinition | null) {
  if (!target) {
    paramModes.value = {}
    return
  }
  const fields = parseDecorationFields(target.paramsSchemaJson || '[]')
  const nextModes: Record<string, 'fixed' | 'binding'> = {}
  for (const field of fields) {
    if (paramBindings.value[field.code]?.trim()) {
      nextModes[field.code] = 'binding'
    } else if (params.value[field.code] !== undefined && params.value[field.code] !== null && params.value[field.code] !== '') {
      nextModes[field.code] = 'fixed'
    } else if (props.autoBindItemParams && target.targetCode !== MINI_PROGRAM_TARGET_CODE) {
      nextModes[field.code] = 'binding'
    } else {
      nextModes[field.code] = 'fixed'
    }
  }
  paramModes.value = nextModes
}

function setParamMode(code: string, mode: 'fixed' | 'binding') {
  paramModes.value = {
    ...paramModes.value,
    [code]: mode,
  }
  if (mode === 'fixed') {
    const nextBindings = { ...paramBindings.value }
    delete nextBindings[code]
    paramBindings.value = nextBindings
  } else {
    if (!paramBindings.value[code]) {
      paramBindings.value = {
        ...paramBindings.value,
        [code]: (props.inferParamBinding ?? defaultBinding)(code),
      }
    }
    const nextParams = { ...params.value }
    delete nextParams[code]
    params.value = nextParams
  }
}

const missingParamLabels = computed(() => {
  if (!selectedTarget.value) return []
  if (selectedTarget.value.targetCode === MINI_PROGRAM_TARGET_CODE) return []
  return paramFields.value
    .filter((field) => {
      const mode = getParamMode(field.code)
      if (mode === 'fixed') {
        const fixed = params.value[field.code]
        return fixed === undefined || fixed === null || fixed === ''
      }
      return !paramBindings.value[field.code]?.trim()
    })
    .map((field) => field.label)
})

const canApply = computed(
  () =>
    Boolean(selectedTarget.value) &&
    (selectedTarget.value?.targetCode === MINI_PROGRAM_TARGET_CODE
      ? !miniProgramError.value
      : missingParamLabels.value.length === 0),
)

const rowSelection = computed<TableRowSelection<LinkTargetRow>>(() => ({
  type: 'radio',
  selectedRowKeys: selectedId.value ? [selectedId.value] : [],
  onChange(keys) {
    const id = String(keys[0] ?? '')
    const row = tableRows.value.find((item) => item.id === id)
    if (row) selectTarget(row)
  },
}))

watch(
  () => props.open,
  (open) => {
    if (!open) return
    selectedCategory.value = 'all'
    keyword.value = ''
    hydrateSelection()
  },
)

function hydrateSelection() {
  const routeParams = miniProgramParamsFromRoute(props.currentRoutePath)
  const current =
    (routeParams ? enabledTargets.value.find((target) => target.targetCode === MINI_PROGRAM_TARGET_CODE) : null) ??
    (props.currentNavigation
      ? linkTargetForNavigation(enabledTargets.value, props.currentNavigation)
      : null) ?? (props.currentRoutePath ? linkTargetByRoutePath(enabledTargets.value, props.currentRoutePath) : null)
  if (!current) {
    selectedId.value = ''
    params.value = {}
    paramBindings.value = {}
    paramModes.value = {}
    return
  }
  selectedId.value = linkTargetIdentity(current)
  params.value = { ...(props.currentNavigation?.params ?? routeParams ?? {}) }
  paramBindings.value = { ...(props.currentNavigation?.paramBindings ?? {}) }
  initParamModes(current)
}

function selectTarget(target: RenDecorationLinkTargetDefinition) {
  selectedId.value = linkTargetIdentity(target)
  params.value = {}
  const fields = parseDecorationFields(target.paramsSchemaJson || '[]')
  const isAutoBind = props.autoBindItemParams && target.targetCode !== MINI_PROGRAM_TARGET_CODE
  paramBindings.value = isAutoBind
    ? Object.fromEntries(fields.map((field) => [field.code, (props.inferParamBinding ?? defaultBinding)(field.code)]))
    : {}
  initParamModes(target)
}

function defaultBinding(code: string) {
  return `item.${code}`
}

function onRowClick(record: LinkTargetRow) {
  selectTarget(record)
}

function setParam(code: string, type: string, value: string) {
  params.value = {
    ...params.value,
    [code]: type === 'number' ? Number(value || 0) : value,
  }
  if (paramBindings.value[code]) {
    const nextBindings = { ...paramBindings.value }
    delete nextBindings[code]
    paramBindings.value = nextBindings
  }
}

function setParamBinding(code: string, value: string) {
  paramBindings.value = {
    ...paramBindings.value,
    [code]: value.trim(),
  }
  if (params.value[code] !== undefined) {
    const nextParams = { ...params.value }
    delete nextParams[code]
    params.value = nextParams
  }
}

function apply() {
  if (!selectedTarget.value || !canApply.value) return
  emit('apply', {
    target: selectedTarget.value,
    params: { ...params.value },
    paramBindings: { ...paramBindings.value },
  })
}

function close() {
  emit('update:open', false)
}

function onOpenChange(open: boolean) {
  emit('update:open', open)
}
</script>

<style scoped>
.ren-link-target-dialog {
  display: grid;
  gap: var(--rr-space-4);
}

.ren-link-target-dialog__toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 240px;
  gap: var(--rr-space-3);
  align-items: center;
}

.ren-link-target-dialog__toolbar :deep(.rr-tabs__nav) {
  margin-bottom: 0;
}

.ren-link-target-dialog__body {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(300px, 0.8fr);
  gap: var(--rr-space-4);
  min-height: 400px;
}

.ren-link-target-dialog__table {
  width: 100%;
  min-width: 0;
}

.ren-link-target-dialog__table :deep(.rr-table-container) {
  overflow-x: hidden;
}

.ren-link-target-dialog__table :deep(table) {
  table-layout: fixed;
  width: 100%;
}

.ren-link-target-dialog__cell-target {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ren-link-target-dialog__name-text {
  font-weight: var(--rr-font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ren-link-target-dialog__group-text {
  font-size: var(--rr-font-size-xs);
  color: var(--rr-color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ren-link-target-dialog__cell-tags {
  display: inline-flex;
  align-items: center;
  gap: var(--rr-space-1);
  flex-wrap: wrap;
}

.ren-link-target-dialog__cell-route {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--rr-font-mono, ui-monospace, monospace);
  font-size: var(--rr-font-size-xs);
  color: var(--rr-color-text-muted);
}

.ren-link-target-dialog__aside {
  min-width: 0;
}

.ren-link-target-dialog__route {
  display: block;
  font-family: var(--rr-font-mono, ui-monospace, monospace);
  font-size: var(--rr-font-size-xs);
  color: var(--rr-color-primary);
  word-break: break-all;
  background: var(--rr-color-surface-subtle);
  padding: var(--rr-space-1) var(--rr-space-2);
  border-radius: var(--rr-radius-sm);
}

.ren-link-target-dialog__param-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.ren-link-target-dialog__param-field {
  width: 100%;
}

.ren-link-target-dialog__param-hint {
  margin: var(--rr-space-1) 0 0 0;
  font-size: var(--rr-font-size-xs);
  color: var(--rr-color-text-muted);
  line-height: 1.4;
}

.ren-link-target-dialog__no-params {
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
  padding: var(--rr-space-2) 0;
}

.ren-link-target-dialog__error {
  color: var(--rr-color-danger);
  font-size: var(--rr-font-size-xs);
}

.ren-link-target-dialog__meta-section {
  display: grid;
  gap: var(--rr-space-1);
  padding-top: var(--rr-space-2);
  margin-top: var(--rr-space-2);
  border-top: 1px solid var(--rr-color-border);
  font-size: var(--rr-font-size-xs);
  color: var(--rr-color-text-muted);
}

.ren-link-target-dialog__meta-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ren-link-target-dialog__prompt {
  margin-top: var(--rr-space-2);
  padding: var(--rr-space-2);
  background: var(--rr-color-surface-subtle);
  border-radius: var(--rr-radius-sm);
  font-size: var(--rr-font-size-xs);
  color: var(--rr-color-warning);
}

.ren-link-target-dialog__aside :deep(.rr-form-item) {
  margin-bottom: var(--rr-space-3);
}

@media (max-width: 840px) {
  .ren-link-target-dialog__toolbar,
  .ren-link-target-dialog__body {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
